import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Trash2, Globe, TriangleAlert, Eye, RefreshCw, Sparkles, Check, Undo2, ShieldAlert } from "lucide-react";
import { purgeArea } from "./Purge";

const FILTERS = [["test", "Obvious test rows"], ["real", "Real-looking"], ["all", "Everything"]];

const fmtWhen = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  return isNaN(d) ? String(s).slice(0, 10) : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" });
};

/** The master reset — every area of the lead-gen admin, with counts and one-click clearing. */
export function AreasTab() {
  const [areas, setAreas] = useState(null);
  const [mode, setMode] = useState("simulated");
  const [sel, setSel] = useState({});
  const [review, setReview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmBulk, setConfirmBulk] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/cleanup/areas");
      setAreas(r.data.areas || []);
      setMode(r.data.public_mode || "simulated");
    } catch { setAreas([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const picked = useMemo(() => (areas || []).filter((a) => sel[a.key] && a.test > 0), [areas, sel]);
  const pickedTotal = picked.reduce((n, a) => n + a.test, 0);

  const wipeSelected = async () => {
    setBusy(true);
    let done = 0;
    for (const a of picked) {
      try {
        const d = await purgeArea(a.key, { mode: "test" });
        done += d.total || 0;
      } catch { toast.error(`${a.label} could not be cleared`); }
    }
    setBusy(false);
    setConfirmBulk(false);
    setSel({});
    toast.success(`${done.toLocaleString()} test rows deleted — recoverable for 30 days`);
    load();
  };

  if (!areas) return <div className="grid place-items-center py-20 text-slate-400"><Loader2 className="size-5 animate-spin" /></div>;

  const grandTest = areas.reduce((n, a) => n + a.test, 0);
  const grandTotal = areas.reduce((n, a) => n + a.total, 0);

  return (
    <div className="space-y-4" data-testid="cleanup-areas">
      <SweepCard onDone={load} />

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="text-[13px] text-slate-600">
          <span className="font-mono text-[17px] font-semibold tabular-nums text-slate-900">{grandTest.toLocaleString()}</span>
          {" "}obvious test rows across {areas.filter((a) => a.test).length} areas,
          out of {grandTotal.toLocaleString()} in total.
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="rounded-full" onClick={load} data-testid="areas-refresh">
            <RefreshCw className="mr-1.5 size-3.5" /> Refresh
          </Button>
          <Button size="sm" className="rounded-full bg-[#0055FF] hover:bg-[#0044CC]"
                  disabled={!pickedTotal} onClick={() => setConfirmBulk(true)} data-testid="areas-wipe-selected">
            <Trash2 className="mr-1.5 size-3.5" />
            {pickedTotal ? `Delete ${pickedTotal.toLocaleString()} test rows in ${picked.length} area${picked.length === 1 ? "" : "s"}` : "Tick some areas"}
          </Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-[10.5px] uppercase tracking-widest text-slate-500">
            <tr>
              <th className="w-10 px-3 py-2.5">
                <input type="checkbox" data-testid="areas-select-all"
                       checked={areas.every((a) => !a.test || sel[a.key])}
                       onChange={(e) => setSel(e.target.checked
                         ? Object.fromEntries(areas.filter((a) => a.test && !a.public).map((a) => [a.key, true]))
                         : {})} />
              </th>
              <th className="px-3 py-2.5">Area</th>
              <th className="px-3 py-2.5 text-right">In here</th>
              <th className="px-3 py-2.5 text-right">Test</th>
              <th className="px-3 py-2.5 text-right">Real-looking</th>
              <th className="px-3 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {areas.map((a) => (
              <tr key={a.key} className="border-t border-slate-100 align-top hover:bg-slate-50/50" data-testid={`area-row-${a.key}`}>
                <td className="px-3 py-3">
                  <input type="checkbox" disabled={!a.test} checked={!!sel[a.key]}
                         onChange={(e) => setSel((s) => ({ ...s, [a.key]: e.target.checked }))}
                         data-testid={`area-check-${a.key}`} />
                </td>
                <td className="px-3 py-3">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="font-medium text-slate-900">{a.label}</span>
                    {a.public && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-[#0055FF]/30 bg-[#0055FF]/[0.07] px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wider text-[#0044CC]"
                            title="The public website counts these rows">
                        <Globe className="size-2.5" /> on your website
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 text-[11.5px] text-slate-500">{a.note}</div>
                </td>
                <td className="px-3 py-3 text-right font-mono tabular-nums text-slate-700">{a.total.toLocaleString()}</td>
                <td className="px-3 py-3 text-right font-mono tabular-nums font-semibold text-slate-900">{a.test ? a.test.toLocaleString() : "—"}</td>
                <td className="px-3 py-3 text-right font-mono tabular-nums text-slate-500">{a.real ? a.real.toLocaleString() : "—"}</td>
                <td className="px-3 py-3 text-right">
                  <Button variant="ghost" size="sm" onClick={() => setReview(a)} data-testid={`area-review-${a.key}`}>
                    <Eye className="mr-1.5 size-3.5" /> Review
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[12px] text-slate-500" data-testid="areas-footnote">
        Ticking &quot;select all&quot; skips the areas your public website can count from — open those
        and decide for yourself. {mode === "simulated"
          ? "Your live numbers are simulated right now, so clearing them wouldn't change connex.is."
          : `Your live numbers are on ${mode}, so those rows really are feeding connex.is.`}
        {" "}Every deletion lands in the recycle bin for 30 days.
      </p>

      <Dialog open={confirmBulk} onOpenChange={setConfirmBulk}>
        <DialogContent className="max-w-md" data-testid="areas-bulk-dialog">
          <DialogHeader>
            <DialogTitle>Delete {pickedTotal.toLocaleString()} test rows?</DialogTitle>
            <DialogDescription>Across {picked.length} area{picked.length === 1 ? "" : "s"}: {picked.map((a) => a.label).join(", ")}.</DialogDescription>
          </DialogHeader>
          {picked.some((a) => a.public) && (
            <div className="flex items-start gap-2 rounded-lg border border-[#0055FF]/30 bg-[#0055FF]/[0.06] p-3 text-[12.5px] text-slate-700">
              <Globe className="mt-0.5 size-4 shrink-0 text-[#0055FF]" />
              <span>Some of these feed the live numbers on connex.is — those counters will drop.</span>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setConfirmBulk(false)}>Cancel</Button>
            <Button onClick={wipeSelected} disabled={busy} className="bg-red-600 hover:bg-red-700" data-testid="areas-bulk-confirm">
              {busy ? <Loader2 className="size-4 animate-spin" /> : "Delete them"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ReviewDialog area={review} onClose={() => setReview(null)} onChanged={load} />
    </div>
  );
}

/** One-click sweep — every obvious test row across the lead-gen side, in one pass. */
function SweepCard({ onDone }) {
  const [info, setInfo] = useState(null);
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState({});
  const [typed, setTyped] = useState("");
  const [withLogs, setWithLogs] = useState(true);
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [undone, setUndone] = useState(false);

  const preview = useCallback(async () => {
    try {
      const r = await api.get("/admin/cleanup/sweep/preview");
      setInfo(r.data);
    } catch { setInfo({ areas: [], total: 0, default_keys: [], default_total: 0 }); }
  }, []);
  useEffect(() => { preview(); }, [preview]);

  const start = () => {
    setReceipt(null);
    setUndone(false);
    setTyped("");
    setWithLogs(true);
    setSel(Object.fromEntries((info?.default_keys || []).map((k) => [k, true])));
    setOpen(true);
  };

  const picked = (info?.areas || []).filter((a) => sel[a.key]);
  const pickedTotal = picked.reduce((n, a) => n + a.test, 0);
  const pickedLogs = withLogs ? picked.reduce((n, a) => n + (a.logs_total || 0), 0) : 0;
  const simulated = info?.public_mode === "simulated";

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/cleanup/sweep", {
        areas: picked.map((a) => a.key), with_logs: withLogs, confirm: "DELETE",
      });
      setReceipt(r.data);
      toast.success(`${(r.data.total || 0).toLocaleString()} test rows swept — recoverable for 30 days`);
      preview();
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sweep failed");
    } finally { setBusy(false); }
  };

  const undo = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/cleanup/recycle/${receipt.recycle_id}/restore`);
      toast.success(`${r.data.restored.toLocaleString()} rows put back`);
      setUndone(true);
      preview();
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not undo the sweep");
    } finally { setBusy(false); }
  };

  if (!info) return null;

  const heldTotal = info.total - info.default_total;
  const nothingLeft = !info.total;

  return (
    <>
      <div className="relative overflow-hidden rounded-2xl border border-[#0055FF]/25 bg-gradient-to-r from-[#0055FF]/[0.05] via-white to-white p-5"
           data-testid="sweep-card">
        <div className="flex flex-wrap items-center gap-4">
          <span className="inline-grid size-11 shrink-0 place-items-center rounded-xl bg-[#0F172A] text-white">
            <Sparkles className="size-5" strokeWidth={1.6} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[9.5px] font-semibold uppercase tracking-[0.2em] text-[#0044CC]">One-click sweep</div>
            <div className="mt-1 text-[15px] font-semibold tracking-[-0.01em] text-slate-900">
              {nothingLeft
                ? "Nothing obvious left to sweep"
                : info.default_total
                  ? <>Clean the whole lead-gen side — <span className="font-mono tabular-nums">{info.default_total.toLocaleString()}</span> obvious test rows across {info.default_keys.length} area{info.default_keys.length === 1 ? "" : "s"}</>
                  : <>The everyday areas are clean — <span className="font-mono tabular-nums">{heldTotal.toLocaleString()}</span> test rows are waiting in the areas held back for you to decide</>}
            </div>
            <div className="mt-1 text-[12.5px] text-slate-500">
              {nothingLeft
                ? <>Every area is clear of rows mentioning test, demo, sample or a demo account. Anything real-looking is left for you to decide by hand below.</>
                : info.default_total
                  ? <>One pass, one receipt telling you exactly what went. The areas your website can count from{info.held_back?.includes("careers") ? " and real job applicants" : ""} are held back unless you tick them yourself. All of it stays recoverable for 30 days.</>
                  : <>Open it and tick the held-back ones if you want them gone too — same receipt, same 30-day undo.</>}
            </div>
          </div>
          <Button onClick={start} disabled={nothingLeft}
                  className="shrink-0 rounded-full bg-[#0055FF] px-5 hover:bg-[#0044CC]" data-testid="sweep-start">
            <Trash2 className="mr-1.5 size-3.5" /> {info.default_total ? "Sweep them all" : "Choose what goes"}
          </Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setReceipt(null); }}>
        <DialogContent className="max-w-lg" data-testid="sweep-dialog">
          {!receipt ? (
            <>
              <DialogHeader>
                <DialogTitle>Sweep the obvious test rows</DialogTitle>
                <DialogDescription>
                  Untick anything you want to keep. Nothing here is real-looking — every row matched
                  test, demo, sample, dummy, example or one of your demo accounts.
                </DialogDescription>
              </DialogHeader>

              <div className={`flex items-start gap-2 rounded-xl border p-3 text-[12.5px] ${
                simulated ? "border-slate-200 bg-slate-50/70 text-slate-600"
                          : "border-[#0055FF]/30 bg-[#0055FF]/[0.06] text-slate-700"}`}
                   data-testid="sweep-public-note">
                <Globe className={`mt-0.5 size-4 shrink-0 ${simulated ? "text-slate-400" : "text-[#0055FF]"}`} />
                {simulated ? (
                  <span>
                    Your public numbers are <b>simulated</b> right now, so nothing you tick here
                    changes connex.is. The <i>on your website</i> areas stay unticked by default
                    anyway — they&apos;d start to matter the day you switch the public activity mode
                    to blended or real.
                  </span>
                ) : (
                  <span>
                    Your public numbers are on <b>{info.public_mode}</b> — the <i>on your website</i>
                    {" "}areas below really are feeding the counters on connex.is.
                  </span>
                )}
              </div>

              <div className="hub-scroll max-h-[40vh] space-y-px overflow-y-auto rounded-xl border border-slate-200">
                {info.areas.map((a) => (
                  <label key={a.key}
                         className="flex cursor-pointer items-center gap-3 border-b border-slate-100 px-3 py-2.5 last:border-0 hover:bg-slate-50/60"
                         data-testid={`sweep-area-${a.key}`}>
                    <input type="checkbox" checked={!!sel[a.key]}
                           onChange={(e) => setSel((s) => ({ ...s, [a.key]: e.target.checked }))}
                           className="accent-[#0055FF]" data-testid={`sweep-check-${a.key}`} />
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-1.5">
                        <span className="text-[13px] font-medium text-slate-900">{a.label}</span>
                        {a.public && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-[#0055FF]/30 bg-[#0055FF]/[0.07] px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-[#0044CC]">
                            <Globe className="size-2.5" /> on your website
                          </span>
                        )}
                        {a.caution && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-[#7da9ff]/45 bg-[#7da9ff]/[0.12] px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-[#3B63A8]">
                            <ShieldAlert className="size-2.5" /> check first
                          </span>
                        )}
                      </span>
                      <span className="mt-0.5 block text-[11.5px] text-slate-500">{a.note}</span>
                    </span>
                    <span className="shrink-0 font-mono text-[13px] font-semibold tabular-nums text-slate-900">
                      {a.test.toLocaleString()}
                    </span>
                  </label>
                ))}
              </div>

              <div className="rounded-xl border border-red-200 bg-red-50/50 p-3">
                <label className="mb-2.5 flex cursor-pointer items-start gap-2 text-[12.5px] text-slate-600">
                  <input type="checkbox" checked={withLogs} onChange={(e) => setWithLogs(e.target.checked)}
                         className="mt-0.5 accent-[#0055FF]" data-testid="sweep-with-logs" />
                  <span>
                    Also clear the attached log tables when an area empties completely
                    {info.logs_total ? <> — <b className="font-mono tabular-nums">{info.logs_total.toLocaleString()}</b> rows of
                    rejections, postbacks and delivery history with nothing left to point at</> : " (nothing orphaned right now)"}
                  </span>
                </label>
                <div className="flex items-start gap-2 text-[12.5px] text-red-800">
                  <TriangleAlert className="mt-0.5 size-4 shrink-0" />
                  <span>
                    You&apos;re about to delete <b className="font-mono tabular-nums">{(pickedTotal + pickedLogs).toLocaleString()}</b> rows
                    from {picked.length} area{picked.length === 1 ? "" : "s"}. Type DELETE to unlock.
                  </span>
                </div>
                <div className="mt-2.5 flex gap-2">
                  <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="DELETE"
                         className="h-9 font-mono" data-testid="sweep-confirm-input" />
                  <Button onClick={run} disabled={typed.trim().toUpperCase() !== "DELETE" || !pickedTotal || busy}
                          className="h-9 shrink-0 bg-red-600 hover:bg-red-700" data-testid="sweep-confirm">
                    {busy ? <Loader2 className="size-4 animate-spin" /> : `Sweep ${(pickedTotal + pickedLogs).toLocaleString()} rows`}
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Check className="size-5 text-teal-600" />
                  {undone ? "Sweep undone — everything is back" : `${receipt.total.toLocaleString()} rows swept`}
                </DialogTitle>
                <DialogDescription>
                  {undone
                    ? "Every row from that sweep has been restored to where it was."
                    : "Here is exactly what went. It is all sitting in the recycle bin for 30 days."}
                </DialogDescription>
              </DialogHeader>

              <div className="overflow-hidden rounded-xl border border-slate-200" data-testid="sweep-receipt">
                {receipt.areas.map((a) => (
                  <div key={a.key} className="border-b border-slate-100 last:border-0"
                       data-testid={`sweep-receipt-${a.key}`}>
                    <div className="flex items-center gap-2 px-3 py-2">
                      <Check className="size-3.5 shrink-0 text-teal-600" />
                      <span className="min-w-0 flex-1 truncate text-[13px] text-slate-700">{a.label}</span>
                      <span className="font-mono text-[12.5px] tabular-nums text-slate-500">{a.collection}</span>
                      <span className="w-16 shrink-0 text-right font-mono text-[13px] font-semibold tabular-nums text-slate-900">
                        {a.deleted.toLocaleString()}
                      </span>
                    </div>
                    {(a.logs || []).map((lg) => (
                      <div key={lg.collection} className="flex items-center gap-2 px-3 pb-2 pl-8">
                        <span className="min-w-0 flex-1 truncate text-[11.5px] text-slate-400">
                          + attached log · {lg.collection.replace(/_/g, " ")}
                        </span>
                        <span className="w-16 shrink-0 text-right font-mono text-[11.5px] tabular-nums text-slate-500">
                          {lg.deleted.toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                ))}
                <div className="flex items-center justify-between bg-slate-50 px-3 py-2">
                  <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Total</span>
                  <span className="font-mono text-[14px] font-semibold tabular-nums text-slate-900" data-testid="sweep-receipt-total">
                    {receipt.total.toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap justify-end gap-2">
                {!undone && receipt.recycle_id && (
                  <Button variant="outline" onClick={undo} disabled={busy} className="mr-auto" data-testid="sweep-undo">
                    {busy ? <Loader2 className="size-4 animate-spin" /> : <><Undo2 className="mr-1.5 size-3.5" /> Undo this sweep</>}
                  </Button>
                )}
                <Button onClick={() => { setOpen(false); setReceipt(null); }} data-testid="sweep-done">Done</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Row-by-row review for one area — decide the real-looking ones by hand. */
function ReviewDialog({ area, onClose, onChanged }) {
  const [filter, setFilter] = useState("test");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => { setFilter("test"); setQ(""); setSel({}); }, [area?.key]);

  const load = useCallback(async () => {
    if (!area) return;
    setData(null);
    try {
      const r = await api.get(`/admin/cleanup/areas/${area.key}?filter=${filter}&q=${encodeURIComponent(q)}&limit=300`);
      setData(r.data);
    } catch { setData({ rows: [], matched: 0 }); }
  }, [area, filter, q]);
  useEffect(() => { load(); }, [load]);

  const ids = Object.keys(sel).filter((k) => sel[k]);

  const del = async () => {
    setBusy(true);
    try {
      const d = await purgeArea(area.key, { mode: "ids", ids });
      toast.success(`${d.total.toLocaleString()} deleted — recoverable for 30 days`);
      setSel({});
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!area} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="area-review-dialog">
        <DialogHeader>
          <DialogTitle>{area?.label}</DialogTitle>
          <DialogDescription>{area?.note}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          {FILTERS.map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`area-filter-${k}`}
                    className={`rounded-full border px-3 py-1 text-[12px] font-medium transition-colors ${
                      filter === k ? "border-[#0055FF] bg-[#0055FF]/[0.07] text-[#0044CC]"
                                   : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>
              {l}
            </button>
          ))}
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
                 className="h-8 w-44" data-testid="area-review-search" />
          <span className="ml-auto text-[12px] text-slate-500" data-testid="area-review-count">
            {data ? `${data.matched.toLocaleString()} match` : "…"}
          </span>
        </div>

        <div className="max-h-[52vh] overflow-y-auto rounded-xl border border-slate-200">
          {!data ? (
            <div className="grid place-items-center py-16 text-slate-400"><Loader2 className="size-5 animate-spin" /></div>
          ) : data.rows.length === 0 ? (
            <div className="py-16 text-center text-[13px] text-slate-400" data-testid="area-review-empty">Nothing here.</div>
          ) : (
            <table className="w-full text-[13px]">
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60" data-testid={`area-review-row-${r.id}`}>
                    <td className="w-9 px-3 py-2.5">
                      <input type="checkbox" checked={!!sel[r.id]}
                             onChange={(e) => setSel((s) => ({ ...s, [r.id]: e.target.checked }))}
                             data-testid={`area-review-check-${r.id}`} />
                    </td>
                    <td className="px-2 py-2.5">
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium text-slate-900">{r.title}</span>
                        {r.test
                          ? <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wider text-slate-500" title={`Matched on: ${r.why.join(", ")}`}>test</span>
                          : <span className="rounded border border-[#7da9ff]/45 bg-[#7da9ff]/[0.12] px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wider text-[#3B63A8]">real-looking</span>}
                      </div>
                      <div className="text-[11.5px] text-slate-500">{[r.subtitle, r.who].filter(Boolean).join(" · ")}</div>
                    </td>
                    <td className="px-2 py-2.5 text-right font-mono text-[12px] tabular-nums text-slate-700">
                      {r.amount != null && r.amount !== "" ? `£${Number(r.amount).toLocaleString()}` : ""}
                    </td>
                    <td className="px-2 py-2.5 text-[11.5px] text-slate-500">{r.status}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-[11.5px] text-slate-400">{fmtWhen(r.when)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-[12px] text-slate-500">
            {ids.length ? `${ids.length} selected` : "Tick the rows you want gone"}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Close</Button>
            <Button onClick={del} disabled={!ids.length || busy} className="bg-red-600 hover:bg-red-700" data-testid="area-review-delete">
              {busy ? <Loader2 className="size-4 animate-spin" /> : <><TriangleAlert className="mr-1.5 size-3.5" /> Delete selected</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
